import React, { useState, useEffect } from 'react';
import {
  TextField,
  Typography,
  Box,
  Grid,
  FormHelperText,
  Checkbox,
  FormControlLabel,
  FormGroup
} from '@mui/material';
import { shades } from '../../../theme';

// Availability Component
const Availability = ({ onValidityChange, onDataChange, initialData = {} }) => {
  const daysOfWeek = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

  const [availability, setAvailability] = useState({
    selectedDays: [],
    startTime: '',
    endTime: '',
    breakStartTime: '',
    breakEndTime: '',
    hasBreak: false
  });

  const [errors, setErrors] = useState({});

  // Load saved data from localStorage when component mounts
  useEffect(() => {
    const storedData = localStorage.getItem('availabilityData');
    if (storedData) {
      const data = JSON.parse(storedData);
      setAvailability(data);
    }
  }, []);

  // Save data to localStorage whenever any field changes
  useEffect(() => {
    localStorage.setItem('availabilityData', JSON.stringify(availability));
  }, []);

  // Validate form
  const validateForm = () => {
    const newErrors = {};
    let isValid = true;

    if (availability.selectedDays.length === 0) {
      newErrors.selectedDays = 'Please select at least one day.';
      isValid = false;
    }

    if (!availability.startTime) {
      newErrors.startTime = 'Start time is required.';
      isValid = false;
    }

    if (!availability.endTime) {
      newErrors.endTime = 'End time is required.';
      isValid = false;
    }

    if (availability.startTime && availability.endTime) {
      const start = new Date(`2000-01-01T${availability.startTime}`);
      const end = new Date(`2000-01-01T${availability.endTime}`);

      if (start >= end) {
        newErrors.endTime = 'End time must be after start time.';
        isValid = false;
      }
    }

    if (availability.hasBreak) {
      if (!availability.breakStartTime) {
        newErrors.breakStartTime = 'Break start time is required.';
        isValid = false;
      }

      if (!availability.breakEndTime) {
        newErrors.breakEndTime = 'Break end time is required.';
        isValid = false;
      }

      if (availability.breakStartTime && availability.breakEndTime) {
        const breakStart = new Date(`2000-01-01T${availability.breakStartTime}`);
        const breakEnd = new Date(`2000-01-01T${availability.breakEndTime}`);

        if (breakStart >= breakEnd) {
          newErrors.breakEndTime = 'Break end time must be after break start time.';
          isValid = false;
        }
      }

      if (availability.startTime && availability.endTime &&
          availability.breakStartTime && availability.breakEndTime) {
        const start = new Date(`2000-01-01T${availability.startTime}`);
        const end = new Date(`2000-01-01T${availability.endTime}`);
        const breakStart = new Date(`2000-01-01T${availability.breakStartTime}`);
        const breakEnd = new Date(`2000-01-01T${availability.breakEndTime}`);

        if (breakStart <= start) {
          newErrors.breakStartTime = 'Break start time must be after day start time.';
          isValid = false;
        }

        if (breakEnd >= end) {
          newErrors.breakEndTime = 'Break end time must be before day end time.';
          isValid = false;
        }
      }
    }

    setErrors(newErrors);
    return isValid;
  };

  // Automatically check form validity whenever any field changes
  useEffect(() => {
    const isValid = validateForm();
    onValidityChange(isValid);
    if (isValid) {
      onDataChange(availability);
    }
  }, [availability, onValidityChange]);

  // Style configuration for text fields
  const textFieldStyle = {
    backgroundColor: '#ffffff',
    '& .MuiOutlinedInput-root': {
      backgroundColor: '#ffffff',
      '& fieldset': {
        borderColor: '#9e9e9e'
      },
      '&:hover fieldset': {
        borderColor: '#1976d2'
      },
      '&.Mui-focused': {
        backgroundColor: '#ffffff !important',
        '& fieldset': {
          borderColor: '#1976d2'
        }
      }
    },
    '& .MuiInputLabel-root': {
      color: '#9e9e9e'
    },
    '& .MuiOutlinedInput-input': {
      color: shades.neutral[700]
    },
    '& input:-webkit-autofill': {
      WebkitBoxShadow: '0 0 0 1000px #ffffff inset',
      transition: 'background-color 5000s ease-in-out 0s'
    }
  };

  const handleChange = (field, value) => {
    setAvailability({
      ...availability,
      [field]: value
    });

    // Clear error for this field if it has a value now
    if (value || (Array.isArray(value) && value.length > 0)) {
      if (errors[field]) {
        setErrors({
          ...errors,
          [field]: ''
        });
      }
    }
  };

  const handleDayToggle = (day) => {
    const selectedDays = [...availability.selectedDays];
    const currentIndex = selectedDays.indexOf(day);

    if (currentIndex === -1) {
      selectedDays.push(day);
    } else {
      selectedDays.splice(currentIndex, 1);
    }

    handleChange('selectedDays', selectedDays);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, color: 'black', height: 'auto' }}>
      <Typography variant='h6' sx={{ color: shades.neutral[700], marginBottom: 1 }}>
        Availability
      </Typography>

      <Box
        sx={{
          border: `1px solid ${shades.neutral[300]}`,
          padding: 3,
          borderRadius: 1
        }}
      >
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Typography variant='subtitle1' gutterBottom>
              Days Available *
            </Typography>
            <FormGroup row>
              {daysOfWeek.map((day) => (
                <FormControlLabel
                  key={day}
                  control={
                    <Checkbox
                      checked={availability.selectedDays.includes(day)}
                      onChange={() => handleDayToggle(day)}
                    />
                  }
                  label={day}
                />
              ))}
            </FormGroup>
            {errors.selectedDays && (
              <FormHelperText error>{errors.selectedDays}</FormHelperText>
            )}
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              label='Start Time *'
              type='time'
              fullWidth
              value={availability.startTime}
              onChange={(e) => handleChange('startTime', e.target.value)}
              error={!!errors.startTime}
              helperText={errors.startTime}
              InputLabelProps={{ shrink: true }}
              sx={textFieldStyle}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              label='End Time *'
              type='time'
              fullWidth
              value={availability.endTime}
              onChange={(e) => handleChange('endTime', e.target.value)}
              error={!!errors.endTime}
              helperText={errors.endTime}
              InputLabelProps={{ shrink: true }}
              sx={textFieldStyle}
            />
          </Grid>

          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={availability.hasBreak}
                  onChange={(e) => handleChange('hasBreak', e.target.checked)}
                />
              }
              label='Include Break Time'
            />
          </Grid>

          {availability.hasBreak && (
            <>
              <Grid item xs={12} sm={6}>
                <TextField
                  label='Break Start Time *'
                  type='time'
                  fullWidth
                  value={availability.breakStartTime}
                  onChange={(e) => handleChange('breakStartTime', e.target.value)}
                  error={!!errors.breakStartTime}
                  helperText={errors.breakStartTime}
                  InputLabelProps={{ shrink: true }}
                  sx={textFieldStyle}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label='Break End Time *'
                  type='time'
                  fullWidth
                  value={availability.breakEndTime}
                  onChange={(e) => handleChange('breakEndTime', e.target.value)}
                  error={!!errors.breakEndTime}
                  helperText={errors.breakEndTime}
                  InputLabelProps={{ shrink: true }}
                  sx={textFieldStyle}
                />
              </Grid>
            </>
          )}
        </Grid>
      </Box>
    </Box>
  );
};

export default Availability;
